import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  CircleNotch, ArrowSquareOut, Warning, CurrencyDollar, Lightning,
} from "@phosphor-icons/react";
import { API } from "@/lib/api";

/**
 * Provider Spend + Top-Up widget for Admin → Billing tab.
 * Reads /api/admin/providers/spend which aggregates last-30-day + this-month
 * spend per provider from our internal cost_events ledger.
 *
 * We also let the admin punch in a "last-known balance" per provider — this
 * value is stored in localStorage only (never server-side). If provided we
 * subtract cumulative spend since to show an estimated remaining balance.
 * If not provided we just show spend + recommended top-up + dashboard link.
 */
const BALANCE_STORAGE_KEY = "tbb_provider_balances_snapshot";

function loadSnapshot() {
  try { return JSON.parse(localStorage.getItem(BALANCE_STORAGE_KEY) || "{}"); }
  catch { return {}; }
}
function saveSnapshot(next) {
  localStorage.setItem(BALANCE_STORAGE_KEY, JSON.stringify(next));
}

export default function ProviderSpendWidget() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [snapshot, setSnapshot] = useState(loadSnapshot);
  const [editing, setEditing] = useState(null); // provider id being edited

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/providers/spend`);
        setData(r.data);
      } catch (e) {
        setErr(e.response?.data?.detail || "Failed to load provider spend");
      }
    })();
  }, []);

  const saveBalance = (pid, balance, at) => {
    const next = { ...snapshot, [pid]: { balance: parseFloat(balance) || 0, at } };
    setSnapshot(next);
    saveSnapshot(next);
    setEditing(null);
  };

  if (err) return <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">{err}</div>;
  if (!data) return <div className="flex items-center justify-center py-6"><CircleNotch size={22} className="animate-spin text-[#E91E8C]" /></div>;

  return (
    <div className="bg-white rounded-2xl border border-zinc-200 p-5" data-testid="provider-spend-widget">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div>
          <p className="text-[10px] uppercase font-bold text-zinc-500 tracking-widest">Provider spend &amp; balances</p>
          <h3 className="text-lg font-bold text-zinc-900 mt-0.5">Where the platform money goes</h3>
          <p className="text-xs text-zinc-500 mt-1">
            Last 30 days: <strong>${data.total_spend_last_30_days_usd.toLocaleString(undefined,{minimumFractionDigits:2})}</strong>
            <span className="mx-2 text-zinc-300">·</span>
            This month: <strong>${data.total_spend_this_month_usd.toLocaleString(undefined,{minimumFractionDigits:2})}</strong>
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {data.providers.map((p) => {
          const snap = snapshot[p.id];
          const est = snap ? Math.max(0, snap.balance - p.spend_last_30_days_usd) : null;
          const daysLeft = snap && p.spend_last_30_days_usd > 0
            ? Math.round((est / (p.spend_last_30_days_usd / 30)))
            : null;
          const low = est !== null && est < p.recommended_topup_usd * 0.25;

          return (
            <div key={p.id} className={`p-4 rounded-xl border ${low ? "border-red-300 bg-red-50/50" : "border-zinc-100 bg-zinc-50/40"}`} data-testid={`provider-spend-${p.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-bold text-zinc-900 text-sm">{p.label}</p>
                  <p className="text-[10px] text-zinc-500 mt-0.5 leading-relaxed">{p.powers}</p>
                </div>
                {low && <Warning size={16} weight="fill" className="text-red-500 flex-shrink-0" />}
              </div>

              <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                <div>
                  <p className="text-[10px] text-zinc-400 uppercase font-bold">Last 30d</p>
                  <p className="text-base font-bold text-zinc-900">${p.spend_last_30_days_usd.toFixed(2)}</p>
                  <p className="text-[10px] text-zinc-500">{p.events_last_30_days} events</p>
                </div>
                <div>
                  <p className="text-[10px] text-zinc-400 uppercase font-bold">This mo.</p>
                  <p className="text-base font-bold text-zinc-900">${p.spend_this_month_usd.toFixed(2)}</p>
                </div>
              </div>

              {/* Balance snapshot */}
              {editing === p.id ? (
                <BalanceEditor pid={p.id} onSave={saveBalance} onCancel={() => setEditing(null)} />
              ) : est !== null ? (
                <div className="mt-3 p-2 rounded-lg bg-white border border-zinc-100">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[10px] text-zinc-400 uppercase font-bold">Est. remaining</p>
                      <p className={`text-lg font-bold ${low ? "text-red-600" : "text-emerald-600"}`}>${est.toFixed(2)}</p>
                      {daysLeft !== null && (
                        <p className="text-[10px] text-zinc-500">~{daysLeft}d at current burn</p>
                      )}
                    </div>
                    <button
                      onClick={() => setEditing(p.id)}
                      className="text-[10px] font-semibold text-[#E91E8C] hover:underline"
                      data-testid={`provider-edit-balance-${p.id}`}
                    >Update</button>
                  </div>
                  <p className="text-[9px] text-zinc-400 mt-1">
                    Snapshot from {new Date(snap.at).toLocaleDateString()}. Stored on this device only.
                  </p>
                </div>
              ) : (
                <button
                  onClick={() => setEditing(p.id)}
                  className="mt-3 w-full text-xs font-semibold text-zinc-600 hover:text-[#E91E8C] py-1.5 border border-dashed border-zinc-200 rounded-lg hover:border-[#E91E8C]"
                  data-testid={`provider-add-balance-${p.id}`}
                >
                  + Log current balance
                </button>
              )}

              <div className="mt-3 flex items-center gap-2 text-xs">
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#E91E8C]/10 text-[#E91E8C] font-bold text-[10px]">
                  <Lightning size={10} weight="fill" /> Recommend ${p.recommended_topup_usd}
                </span>
                {p.dashboard_url && (
                  <a
                    href={p.dashboard_url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-zinc-600 hover:text-[#E91E8C] font-semibold text-[10px]"
                    data-testid={`provider-topup-link-${p.id}`}
                  >
                    Top up <ArrowSquareOut size={10} />
                  </a>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <p className="text-[10px] text-zinc-400 mt-4">
        Recommended top-up = 60 days of runway based on your last-30-days burn rate. Providers don&apos;t expose live balance APIs, so paste your current balance to see time-to-empty.
      </p>
    </div>
  );
}

function BalanceEditor({ pid, onSave, onCancel }) {
  const [val, setVal] = useState("");
  return (
    <div className="mt-3 p-2 rounded-lg bg-white border border-[#E91E8C]/30">
      <p className="text-[10px] text-zinc-500 mb-1.5">Current balance (from provider dashboard):</p>
      <div className="flex items-center gap-1">
        <CurrencyDollar size={14} className="text-zinc-400" />
        <input
          type="number"
          step="0.01"
          value={val}
          onChange={(e) => setVal(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") onSave(pid, val, new Date().toISOString()); }}
          placeholder="150.00"
          data-testid={`provider-balance-input-${pid}`}
          autoFocus
          className="flex-1 border border-zinc-200 rounded px-2 py-1 text-sm"
        />
      </div>
      <div className="flex items-center gap-1 mt-2">
        <button
          onClick={() => onSave(pid, val, new Date().toISOString())}
          disabled={!val}
          data-testid={`provider-balance-save-${pid}`}
          className="flex-1 py-1 rounded bg-[#E91E8C] text-white text-xs font-semibold hover:bg-[#c91876] disabled:opacity-50"
        >Save</button>
        <button
          onClick={onCancel}
          className="flex-1 py-1 rounded border border-zinc-200 text-zinc-700 text-xs font-semibold"
        >Cancel</button>
      </div>
    </div>
  );
}
